import { PitStop } from './constants'

export type PitStopStatus = 'upcoming' | 'active' | 'closed'

// The campaign runs on Mexico City civil dates: a user browsing at 23:00 on
// Sunday anywhere in the world must not see Monday's pit stop yet. Instead
// of comparing UTC timestamps, we resolve "today" in America/Mexico_City via
// Intl (the same tz database dayjs/timezone relies on, without registering
// global plugins) and compare YYYY-MM-DD strings lexicographically.
const MX_TIME_ZONE = 'America/Mexico_City'

// PS4 kickoff: from this date PS1-3 stop accepting new participants
// (issue rule "no new participants after PS3"). The backend-driven
// exception — users who already participate may finish — is a Phase 3+
// dependency and is intentionally not modeled here.
const FINAL_STAGE_START = '2026-10-19'

/** Civil date (YYYY-MM-DD) in Mexico City for the given instant */
export const currentDateInMexico = (now: Date = new Date()): string => {
  // Assembled from formatToParts instead of format(): CLDR does not
  // guarantee the en-CA pattern is YYYY-MM-DD (Chromium/ICU regressions
  // have shipped M/D/YYYY), and a silent pattern change would break the
  // lexicographic comparison below.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MX_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Schedule-only status of a pit stop at the given instant */
export const getPitStopStatus = (pitStop: PitStop, now: Date = new Date()): PitStopStatus => {
  const today = currentDateInMexico(now)
  if (today < pitStop.startsAt) return 'upcoming'
  if (pitStop.key !== 'ranking' && today >= FINAL_STAGE_START) return 'closed'
  return 'active'
}

/**
 * Presentation status: an active module whose dynamic has no route yet
 * (backend id still null) degrades to 'upcoming' so the UI never renders
 * a broken link.
 */
export const getPitStopViewStatus = (
  pitStop: PitStop,
  route: string | null,
  now: Date = new Date()
): PitStopStatus => {
  const status = getPitStopStatus(pitStop, now)
  if (status === 'active' && route === null) return 'upcoming'
  return status
}
